import { useEffect, useRef, useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Button } from '@/components/ui/button';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;

export const WebGoogleButton = ({ nonce, onCredential, onError, disabled }) => {
  const container = useRef(null);
  const [width, setWidth] = useState(280);
  const [scriptError, setScriptError] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const element = container.current;
    const resize = new ResizeObserver(([entry]) => setWidth(Math.min(400, Math.floor(entry.contentRect.width))));
    resize.observe(element);
    const markControls = () => {
      element.querySelector('[role="button"]')?.setAttribute('data-testid', 'auth-google-web-button');
      element.querySelector('iframe')?.setAttribute('data-testid', 'auth-google-web-iframe');
    };
    const observer = new MutationObserver(markControls);
    observer.observe(element, { childList: true, subtree: true });
    return () => { resize.disconnect(); observer.disconnect(); };
  }, []);

  useEffect(() => {
    if (scriptReady) return;
    const timeout = setTimeout(() => setScriptError(true), 15000);
    return () => clearTimeout(timeout);
  }, [attempt, scriptReady]);

  return (
    <div ref={container} className="relative w-full min-w-0 min-h-11" data-testid="auth-google-web-control" aria-busy={!scriptReady && !scriptError}>
      {!CLIENT_ID ? (
        <p role="alert" className="text-sm text-red-300" data-testid="auth-google-config-error">Google sign-in is not configured. Please use email sign-in.</p>
      ) : scriptError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-300" data-testid="auth-google-script-error">Google could not load. Check your connection or use email sign-in.</p>
          <Button type="button" variant="outline" className="w-full h-11 rounded-xl" data-testid="auth-google-script-retry" onClick={() => { setScriptError(false); setScriptReady(false); setAttempt(value => value + 1); }}>Retry Google sign-in</Button>
        </div>
      ) : (
        <GoogleOAuthProvider key={attempt} clientId={CLIENT_ID} onScriptLoadSuccess={() => { setScriptReady(true); setScriptError(false); }} onScriptLoadError={() => setScriptError(true)}>
          {!scriptReady && <p className="text-sm text-white/60 py-3 text-center" data-testid="auth-google-script-loading">Loading Google sign-in…</p>}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className={disabled ? 'pointer-events-none opacity-50' : ''} aria-disabled={disabled} inert={disabled ? true : undefined}>
            <GoogleLogin key={`${nonce}-${width}`} nonce={nonce} onSuccess={response => onCredential(response.credential)} onError={onError}
              auto_select={false} useOneTap={false} ux_mode="popup" text="continue_with" theme="outline" shape="pill" size="large" width={width} />
          </div>
        </GoogleOAuthProvider>
      )}
    </div>
  );
};